// A minimal .xlsx WRITER: a few sheets of strings and numbers, a bold header
// row, column widths, and a money format. Nothing more.
//
// WHY NOT A LIBRARY: the same reason `backend/src/domain/xlsx.ts` gives for its
// reader — SheetJS's last npm release carries unpatched advisories. An .xlsx is
// a zip of XML, and a rectangular sheet of plain cells is a small, bounded
// writing problem: `fflate` (the reader's own zip library) does the zip.
//
// DELIBERATELY NOT SUPPORTED: formulas, dates as Excel dates (they are written
// as the text the screen shows), merged cells, colours. If an export ever needs
// those, reach for a real library rather than growing this.
import { strToU8, zipSync } from 'fflate'

/** A cell: text, a number, or a number to show as money. `null` is blank. */
export type Cell = string | number | { money: number } | null

interface SheetSpec {
  /** Shown on the tab. Trimmed to Excel's 31 characters and its banned characters removed. */
  name: string
  /** Bold, first row. */
  header: readonly string[]
  rows: readonly (readonly Cell[])[]
  /** Width per column, in characters. */
  widths?: readonly number[]
  /** Plain lines written ABOVE the header — a title and the filters, say. */
  preamble?: readonly string[]
}

const XML_HEAD = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n'

/** Escape for XML text, and drop the control characters XML 1.0 forbids. */
function esc(text: string): string {
  return text
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/** Column index (0-based) -> letters: 0 -> A, 26 -> AA. */
function columnName(index: number): string {
  let n = index + 1
  let name = ''
  while (n > 0) {
    const rem = (n - 1) % 26
    name = String.fromCharCode(65 + rem) + name
    n = Math.floor((n - 1) / 26)
  }
  return name
}

/** Excel refuses a sheet name over 31 characters or with any of `[]:*?/\`. */
function sheetName(name: string): string {
  const cleaned = name.replace(/[[\]:*?/\\]/g, ' ').trim().slice(0, 31)
  return cleaned === '' ? 'Sheet' : cleaned
}

// Style indexes into `cellXfs` below: 0 plain, 1 bold, 2 money.
const STYLE_BOLD = 1
const STYLE_MONEY = 2

function cellXml(ref: string, value: Cell, style = 0): string {
  if (value === null) return ''
  const s = style === 0 ? '' : ` s="${style}"`
  if (typeof value === 'number') {
    return Number.isFinite(value) ? `<c r="${ref}"${s}><v>${value}</v></c>` : ''
  }
  if (typeof value === 'object') {
    return Number.isFinite(value.money) ? `<c r="${ref}" s="${STYLE_MONEY}"><v>${value.money}</v></c>` : ''
  }
  // A leading = + - @ would be read as a formula by some spreadsheet programs
  // when a user retypes the cell; inline strings are never evaluated on open,
  // so the value is written as it stands.
  return `<c r="${ref}" t="inlineStr"${s}><is><t xml:space="preserve">${esc(value)}</t></is></c>`
}

function sheetXml(sheet: SheetSpec): string {
  const lines: string[] = []
  let r = 1
  for (const line of sheet.preamble ?? []) {
    lines.push(`<row r="${r}">${cellXml(`A${r}`, line, r === 1 ? STYLE_BOLD : 0)}</row>`)
    r++
  }
  if ((sheet.preamble ?? []).length > 0) r++ // one blank row before the table
  const headerRow = r
  lines.push(
    `<row r="${r}">${sheet.header.map((h, c) => cellXml(`${columnName(c)}${r}`, h, STYLE_BOLD)).join('')}</row>`,
  )
  r++
  for (const row of sheet.rows) {
    lines.push(`<row r="${r}">${row.map((cell, c) => cellXml(`${columnName(c)}${r}`, cell)).join('')}</row>`)
    r++
  }
  const cols =
    sheet.widths && sheet.widths.length > 0
      ? `<cols>${sheet.widths.map((w, i) => `<col min="${i + 1}" max="${i + 1}" width="${w}" customWidth="1"/>`).join('')}</cols>`
      : ''
  // The header row stays in view while scrolling.
  const pane = `<sheetViews><sheetView workbookViewId="0"><pane ySplit="${headerRow}" topLeftCell="A${headerRow + 1}" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>`
  return (
    XML_HEAD +
    '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
    pane +
    cols +
    `<sheetData>${lines.join('')}</sheetData>` +
    '</worksheet>'
  )
}

const STYLES =
  XML_HEAD +
  '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
  '<numFmts count="1"><numFmt numFmtId="164" formatCode="&quot;$&quot;#,##0.00"/></numFmts>' +
  '<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts>' +
  '<fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>' +
  '<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>' +
  '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>' +
  '<cellXfs count="3">' +
  '<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>' +
  '<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>' +
  '<xf numFmtId="164" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/>' +
  '</cellXfs>' +
  '</styleSheet>'

/** Build the workbook's bytes. Sheet names are made unique and Excel-safe. */
export function buildWorkbook(sheets: readonly SheetSpec[]): Uint8Array {
  const names: string[] = []
  for (const sheet of sheets) {
    const base = sheetName(sheet.name)
    let name = base
    for (let n = 2; names.includes(name); n++) name = `${base.slice(0, 28)} ${n}`
    names.push(name)
  }
  const files: Record<string, Uint8Array> = {
    '[Content_Types].xml': strToU8(
      XML_HEAD +
        '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">' +
        '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
        '<Default Extension="xml" ContentType="application/xml"/>' +
        '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
        '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>' +
        sheets
          .map(
            (_, i) =>
              `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`,
          )
          .join('') +
        '</Types>',
    ),
    '_rels/.rels': strToU8(
      XML_HEAD +
        '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
        '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>' +
        '</Relationships>',
    ),
    'xl/workbook.xml': strToU8(
      XML_HEAD +
        '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">' +
        `<sheets>${names.map((name, i) => `<sheet name="${esc(name)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`).join('')}</sheets>` +
        '</workbook>',
    ),
    'xl/_rels/workbook.xml.rels': strToU8(
      XML_HEAD +
        '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
        sheets
          .map(
            (_, i) =>
              `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`,
          )
          .join('') +
        `<Relationship Id="rId${sheets.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>` +
        '</Relationships>',
    ),
    'xl/styles.xml': strToU8(STYLES),
  }
  sheets.forEach((sheet, i) => {
    files[`xl/worksheets/sheet${i + 1}.xml`] = strToU8(sheetXml(sheet))
  })
  return zipSync(files)
}
